import { useQuery } from 'react-query';
import { INFLIGHT_URL } from 'services/apis/config';
import { FlightCatering } from './types';
import axios from 'axios';
import { useRef, useState } from 'react';

const getFlightCateringManagement = async (): Promise<FlightCatering | null> => {
  const params = new URLSearchParams(window.location.search);
  const plusMinutes = params.get('plusMinutes') || '';
  const plusHours = params.get('plusHours') || '';

  const fetchData = (token: string) =>
    axios.get<FlightCatering>(`${INFLIGHT_URL}/api/dashboard/catering`, {
      params: { plusMinutes, plusHours },
      headers: { Authorization: `Bearer ${token}` },
    });

  try {
    const token = localStorage.getItem('accessToken') || '';
    const res = await fetchData(token);
    return res.data;
  } catch {
    try {
      const newToken = await refreshTheToken();
      const retryRes = await fetchData(newToken);
      return retryRes.data;
    } catch {
      return null;
    }
  }
};

export const useGetFlights = () => {
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const previousData = useRef<FlightCatering | null>(null);
  const failureCount = useRef(0);

  const query = useQuery<FlightCatering | null>(
    ['flightCateringManagements'],
    async () => {
      const result = await getFlightCateringManagement();

      if (result === null) {
        failureCount.current += 1;
        if (failureCount.current >= 3) setShowErrorPopup(true);
      } else {
        failureCount.current = 0;
        previousData.current = result;
        setShowErrorPopup(false);
      }

      return result;
    },
    {
      enabled: !showErrorPopup,
      refetchInterval: false,
      refetchIntervalInBackground: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
      retry: false,
    }
  );

  return {
    ...query,
    flightData: previousData.current,
    showErrorPopup,
    hasPreviousData: previousData.current !== null,
    dismissPopup: () => setShowErrorPopup(false),
    goHome: logout,
  };
};

const refreshTheToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    logout();
    throw new Error('No refresh token');
  }

  try {
    const response = await axios.get(`${INFLIGHT_URL}/api/rbac/authenticate/refresh`, {
      params: { refreshToken },
    });
    localStorage.setItem('accessToken', response.data.token.accessToken);
    localStorage.setItem('refreshToken', response.data.token.refreshToken);
    localStorage.setItem('isConnected', 'true');
    localStorage.setItem('expiration', response.data.token.tokenExpiresIn);
    return response.data.token.accessToken;
  } catch {
    logout();
    throw new Error('Refresh failed');
  }
};

const logout = () => {
  localStorage.clear();
  window.location.href = '/login';
};
